<template>
    <div
        class="h-15 flex items-center justify-between dark:bg-[var(--el-bg-color-overlay)] bg-white shadow-[0_1px_6px_-5px_var(--el-text-color-primary)]"
    >
        <!-- 左侧 -->
        <div class="flex items-center">
            <!-- 移动端汉堡菜单 -->
            <div
                class="md:hidden flex items-center justify-center w-9 h-9 ml-2 cursor-pointer"
                @click="$emit('toggle-menu')"
            >
                <el-icon :size="20"><Fold /></el-icon>
            </div>
            <!-- 搜索框 -->
            <div class="mx-3 md:mx-5">
                <el-input
                    v-model="searchKeyword"
                    placeholder="搜索"
                    :prefix-icon="Search"
                    class="header-search-input"
                    clearable
                />
            </div>
        </div>

        <!-- 右侧：功能入口 -->
        <div class="flex items-center">
            <!-- 帮助中心 -->
            <el-tooltip content="帮助中心" placement="bottom">
                <div class="header-icon-btn" @click="openHelpCenter">
                    <el-icon :size="18"><QuestionFilled /></el-icon>
                </div>
            </el-tooltip>

            <!-- 消息 -->
            <el-popover placement="bottom" :width="340" trigger="click" @show="handlePanelShow">
                <template #reference>
                    <div class="header-icon-btn">
                        <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99">
                            <el-icon :size="18"><Bell /></el-icon>
                        </el-badge>
                    </div>
                </template>
                <div class="message-panel">
                    <div class="message-header">
                        <span class="font-bold">消息通知</span>
                        <div class="flex items-center gap-1">
                            <el-tag v-if="messageStore.wsConnected" type="success" size="small" effect="plain">
                                实时
                            </el-tag>
                            <el-button
                                type="primary"
                                link
                                size="small"
                                :disabled="unreadCount === 0"
                                @click="handleReadAll"
                            >
                                全部已读
                            </el-button>
                        </div>
                    </div>

                    <!-- 首次加载中：骨架占位（待办/通知并发拉取，齐了一次性渲染，避免闪烁） -->
                    <div v-if="panelLoading" class="panel-loading">
                        <el-skeleton :rows="3" animated />
                    </div>

                    <!-- 待办为空且通知为空：统一友好空态 -->
                    <div v-else-if="panelEmpty" class="panel-empty">
                        <div class="empty-illustration">
                            <el-icon :size="30"><Bell /></el-icon>
                        </div>
                        <div class="empty-title">暂无新消息</div>
                        <div class="empty-sub">审批与系统通知将在这里提醒您</div>
                        <el-button type="primary" link size="small" @click="goApprovalCenter">去审批中心</el-button>
                    </div>

                    <template v-else>
                        <!-- 待办区：待我审批入口 + 最近 3 条待办 -->
                        <div class="panel-section">
                            <div class="approval-entry" @click="goApprovalCenter">
                                <span class="approval-label">
                                    待我审批
                                    <el-badge
                                        v-if="wfStore.pendingCount > 0"
                                        :value="wfStore.pendingCount"
                                        :max="99"
                                        class="ml-2"
                                    />
                                </span>
                                <el-icon :size="14"><ArrowRight /></el-icon>
                            </div>
                            <template v-if="todoPreview.length">
                                <div
                                    v-for="item in todoPreview"
                                    :key="item.taskId || item.instanceId"
                                    class="todo-item"
                                    @click="goApprovalDetail(item)"
                                >
                                    <span class="todo-title">{{ item.title || '审批单' }}</span>
                                    <span class="todo-time">{{ formatNoticeTime(item.createTime) }}</span>
                                </div>
                            </template>
                            <div v-else class="section-empty">暂无待办</div>
                        </div>

                        <!-- 通知区：会话摘要（最近 5 条，类型图标 + 相对时间 + 未读高亮） -->
                        <div class="panel-section">
                            <div class="section-title">通知</div>
                            <template v-if="noticeList.length">
                                <div
                                    v-for="item in noticeList"
                                    :key="item.conversationId"
                                    class="message-item cursor-pointer"
                                    @click="handleNoticeClick(item)"
                                >
                                    <div class="type-icon" :class="`type-${item.type || 3}`">
                                        <el-icon :size="15"><component :is="convIcon(item.type)" /></el-icon>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <div class="message-title" :class="{ unread: item.unreadNum }">
                                            <span class="title-text">{{ item.title || '系统通知' }}</span>
                                            <i v-if="item.unreadNum" class="unread-dot" />
                                            <el-badge
                                                v-if="item.unreadNum && item.unreadNum > 1"
                                                :value="item.unreadNum"
                                                :max="99"
                                                class="ml-1"
                                            />
                                        </div>
                                        <div class="message-content">{{ item.lastMsg || '-' }}</div>
                                    </div>
                                    <div class="message-time">{{ formatNoticeTime(item.lastMsgTime) }}</div>
                                </div>
                            </template>
                            <div v-else class="section-empty">暂无通知</div>
                        </div>
                    </template>

                    <div class="message-footer">
                        <el-button type="primary" link size="small" @click="goMessageCenter()">查看全部</el-button>
                    </div>
                </div>
            </el-popover>

            <!-- 最近访问 -->
            <el-popover placement="bottom" :width="280" trigger="click">
                <template #reference>
                    <div class="header-icon-btn">
                        <el-icon :size="18"><Clock /></el-icon>
                    </div>
                </template>
                <div class="recent-panel">
                    <div class="recent-header font-bold mb-2">最近访问</div>
                    <el-empty v-if="recentVisits.length === 0" description="暂无访问记录" :image-size="60" />
                    <div v-else class="recent-list">
                        <div
                            v-for="(item, index) in recentVisits"
                            :key="index"
                            class="recent-item"
                            @click="goToPage(item.path)"
                        >
                            <span class="recent-label">{{ item.label }}</span>
                            <span class="recent-time">{{ item.timeAgo }}</span>
                        </div>
                    </div>
                </div>
            </el-popover>

            <!-- 常用功能 -->
            <el-popover placement="bottom" :width="200" trigger="click">
                <template #reference>
                    <div class="header-icon-btn">
                        <el-icon :size="18"><Grid /></el-icon>
                    </div>
                </template>
                <div class="quick-panel">
                    <div class="quick-header font-bold mb-2">常用功能</div>
                    <div class="quick-grid">
                        <div
                            v-for="item in quickEntries"
                            :key="item.name"
                            class="quick-item"
                            @click="handleQuickEntry(item)"
                        >
                            <el-icon :size="20"><component :is="item.icon" /></el-icon>
                            <span>{{ item.label }}</span>
                        </div>
                    </div>
                </div>
            </el-popover>

            <!-- 暗黑模式切换 -->
            <el-tooltip :content="isDark ? '切换亮色模式' : '切换暗黑模式'" placement="bottom">
                <div class="header-icon-btn" @click="toggleDark()">
                    <el-icon :size="18">
                        <Sunny v-if="isDark" />
                        <Moon v-else />
                    </el-icon>
                </div>
            </el-tooltip>

            <!-- 用户信息 -->
            <el-dropdown trigger="click" class="ml-2 mr-5">
                <div class="user-info-btn">
                    <div class="avatar">
                        <TpSvgIcon name="avatar" :size="32" />
                    </div>
                    <span class="username" v-if="userStore.userInfo">
                        {{ userStore.userInfo.userName || userStore.userInfo.mobileNo || '用户' }}
                    </span>
                    <el-icon><ArrowDown /></el-icon>
                </div>
                <template #dropdown>
                    <el-dropdown-menu>
                        <el-dropdown-item @click="goToProfile">
                            <el-icon><User /></el-icon>
                            用户信息
                        </el-dropdown-item>
                        <el-dropdown-item @click="refreshPermissions">
                            <el-icon><Refresh /></el-icon>
                            刷新权限
                        </el-dropdown-item>
                        <el-dropdown-item divided @click="logout">
                            <el-icon><SwitchButton /></el-icon>
                            退出登录
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { useDark } from '@vueuse/core';
import { dayjs } from 'element-plus';
import { useUserStore } from '@/stores/user';
import {
    QuestionFilled,
    Bell,
    BellFilled,
    ChatDotRound,
    ChatLineRound,
    Clock,
    Grid,
    Sunny,
    Moon,
    ArrowDown,
    ArrowRight,
    User,
    SwitchButton,
    Refresh,
    Document,
    ShoppingCart,
    Goods,
    Search,
    Fold
} from '@element-plus/icons-vue';

import router from '@/router';

import { toggleDark } from '@/composables';
import { useMessageStore } from '@/stores/message';
import { useWfStore } from '@/stores/wf';
import { getWfTodoPage } from '@/api/wf';
import { isWfNotify, pushByNotifyLink, resolveWfInstanceId } from '@/utils/im/wfNotify';

const emit = defineEmits<{
    (e: 'toggle-menu'): void;
}>();

const isDark = useDark();

// 用户 Store
const userStore = useUserStore();

// 消息中心 Store（未读 = 服务端汇总口径，实时更新由 xqyy_msg 长连接驱动）
const messageStore = useMessageStore();

// 审批中心 Store（待我审批角标 = /wf/getTodoPage totalCount，同样读服务端口径）
const wfStore = useWfStore();

// 搜索关键字
const searchKeyword = ref('');

// 消息相关：角标读服务端汇总，列表取会话摘要前 5 条（禁止本地加减未读）
const unreadCount = computed(() => messageStore.totalUnread);
const noticeList = computed(() => messageStore.conversations.slice(0, 5));

// 待办区：面板打开时拉最近 3 条待办（不常驻轮询，角标由 wfStore 长连接驱动）
const todoPreview = ref<Wf.ITaskTodo[]>([]);
const todoLoaded = ref(false);
// 通知区：会话首次加载完成标记（只门控首次渲染，避免长连接刷新时面板闪骨架）
const noticeFirstLoaded = ref(false);

/** 首次加载完成前的统一占位：待办 + 通知都在飞，避免「半成品结构 → 空态」两次跳变 */
const panelLoading = computed(() => !todoLoaded.value || !noticeFirstLoaded.value);

/** 待办与通知全空时的统一空态（待办数以服务端口径为准，避免加载期闪烁） */
const panelEmpty = computed(() => !panelLoading.value && !todoPreview.value.length && !noticeList.value.length);

/** 面板打开：刷新待办角标 + 拉取最近 3 条待办标题 + 刷新会话摘要 */
async function handlePanelShow() {
    wfStore.refreshPendingCount();
    todoLoaded.value = false;
    noticeFirstLoaded.value = false;
    await Promise.all([
        getWfTodoPage({ pageNo: 1, pageSize: 3 })
            .then(({ result }) => {
                todoPreview.value = ((result?.result || []) as Wf.ITaskTodo[]).slice(0, 3);
            })
            .catch(() => {
                todoPreview.value = [];
            })
            .finally(() => {
                todoLoaded.value = true;
            }),
        messageStore
            .loadConversations(1, 20)
            .catch(() => undefined)
            .finally(() => {
                noticeFirstLoaded.value = true;
            })
    ]);
}

/** 会话类型图标：3 系统通知 / 1 单聊 / 2 群聊 */
function convIcon(type?: number) {
    if (type === 3) return BellFilled;
    if (type === 2) return ChatLineRound;
    return ChatDotRound;
}

/** 通知时间：3 分钟内「刚刚」/ 今天 HH:mm / 昨天「昨天」/ 更早 YYYY-MM-DD */
function formatNoticeTime(time?: string) {
    if (!time) return '';
    const target = dayjs(time);
    if (!target.isValid()) return time;
    const now = dayjs();
    if (now.diff(target, 'minute') < 3) return '刚刚';
    if (target.isSame(now, 'day')) return target.format('HH:mm');
    if (target.isSame(now.subtract(1, 'day'), 'day')) return '昨天';
    return target.format('YYYY-MM-DD');
}

/** 待办点击：直达审批详情 */
function goApprovalDetail(item: Wf.ITaskTodo) {
    if (!item.instanceId) return;
    router.push({ path: '/admin/approval/index', query: { instanceId: String(item.instanceId), autoOpen: '1' } });
}

/**
 * 通知点击：解析会话最近一条帧的 extraData——
 * 审批类通知（notifyType = WF_*）直达审批中心并标记该会话已读；
 * 其余消息维持现状进消息中心。历史消息无 extraData（落库不存），走消息中心。
 */
function handleNoticeClick(item: Message.IConversation) {
    const extra = messageStore.notifyExtras[item.conversationId];
    if (extra && isWfNotify(extra)) {
        // 解析链第一级：帧内持久化口径 link（与落库 chs_message.link 同格式），非空直接站内跳转
        if (pushByNotifyLink(extra.link)) {
            messageStore.markRead(item.conversationId);
            return;
        }
        const instanceId = resolveWfInstanceId(extra);
        const query: Record<string, string> = { autoOpen: '1' };
        if (instanceId) {
            query.instanceId = String(instanceId);
        } else if (extra.bizType && extra.bizId) {
            // 工作流通知的 bizId 是业务单据 id：交给审批中心页按 bizType + bizId 反查实例
            query.bizType = String(extra.bizType);
            query.bizId = String(extra.bizId);
        } else {
            goMessageCenter(item.conversationId);
            return;
        }
        messageStore.markRead(item.conversationId);
        router.push({ path: '/admin/approval/index', query });
        return;
    }
    goMessageCenter(item.conversationId);
}

// 最近访问
const recentVisits = ref<{ path: string; label: string; timeAgo: string }[]>([]);

// 常用功能快捷入口
const quickEntries = ref([
    { name: 'orderList', label: '订单管理', icon: Document, path: '/admin/order/list' },
    { name: 'productPublish', label: '产品发布', icon: Goods, path: '/admin/product/publish' }
]);

// 帮助中心
function openHelpCenter() {
    // NOTE: 帮助中心地址待配置
    ElMessage.info('帮助中心功能开发中');
}

// 刷新权限
async function refreshPermissions() {
    try {
        await userStore.refreshMenus();
    } catch (e: any) {
        ElMessage.error(e?.msg || '权限刷新失败');
    }
}

// 跳转页面
function goToPage(path: string) {
    router.push({ path });
}

// 打开通知中心（可指定会话；未读由通知中心页标记已读后刷新）
function goMessageCenter(conversationId?: string) {
    router.push({
        path: '/admin/message/index',
        query: conversationId ? { conversationId } : {}
    });
}

// 打开审批中心（待我审批入口）
function goApprovalCenter() {
    router.push({ path: '/workflow' });
}

// 全部已读（服务端接口，随后刷新角标）
async function handleReadAll() {
    await messageStore.markAllRead();
}

// 快捷入口点击
function handleQuickEntry(item: { path: string }) {
    router.push({ path: item.path });
}

// 个人资料 - 跳转到账号信息页
function goToProfile() {
    router.push({ path: '/admin/account/info' });
}

// 退出登录
function logout() {
    // 断开长连接并清空消息缓存，避免下一个账号看到上一个账号的未读
    messageStore.stop();
    messageStore.reset();
    // 审批中心：解除帧订阅并清空待办角标（同一账号切换商家时也不串号）
    wfStore.stop();
    wfStore.reset();
    userStore.logout();
    router.push({ path: '/login' });
}

// 加载最近访问记录
function loadRecentVisits() {
    try {
        const stored = localStorage.getItem('recentVisits');
        if (stored) {
            const visits = JSON.parse(stored) as { path: string; label: string; timestamp: number }[];
            const now = Date.now();
            recentVisits.value = visits.slice(0, 10).map((item) => ({
                path: item.path,
                label: item.label,
                timeAgo: formatTimeAgo(now - item.timestamp)
            }));
        }
    } catch (e) {
        // 本地记录损坏时忽略，重新访问后会重建
    }
}

// 格式化时间差
function formatTimeAgo(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}小时前`;
    const days = Math.floor(hours / 24);
    return `${days}天前`;
}

// 监听路由变化，记录访问历史
function setupRouteWatcher() {
    router.afterEach((to) => {
        if (to.meta?.label && to.path.startsWith('/admin')) {
            try {
                const stored = localStorage.getItem('recentVisits');
                let visits: { path: string; label: string; timestamp: number }[] = stored ? JSON.parse(stored) : [];

                // 移除重复项
                visits = visits.filter((item) => item.path !== to.path);

                // 添加新记录到开头
                visits.unshift({
                    path: to.path,
                    label: to.meta.label as string,
                    timestamp: Date.now()
                });

                // 只保留最近 10 条
                visits = visits.slice(0, 10);

                localStorage.setItem('recentVisits', JSON.stringify(visits));
                loadRecentVisits();
            } catch (e) {
                // 写入失败不影响路由跳转
            }
        }
    });
}

onMounted(() => {
    loadRecentVisits();
    setupRouteWatcher();
    // 角标以服务端汇总为准；长连接由 layout.vue 统一启动（messageStore.start）
    messageStore.refreshSummary();
});

onUnmounted(() => {});
</script>

<style lang="scss" scoped>
.header-search-input {
    width: 140px;
}
@media (min-width: 768px) {
    .header-search-input {
        width: 240px;
    }
}

.header-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    margin: 0 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: var(--el-fill-color-light);
    }
}

.user-info-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: var(--el-fill-color-light);
    }

    .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        overflow: hidden;
    }

    .username {
        font-size: 14px;
        color: var(--el-text-color-primary);
    }
}

.message-panel {
    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--el-border-color-lighter);
        margin-bottom: 12px;
    }

    // 首次加载占位：骨架屏，待办与通知并发拉取完成后一次性切换，避免结构跳变
    .panel-loading {
        padding: 6px 0 10px;
    }

    // 统一空态：待办与通知均为空
    .panel-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 22px 0 18px;

        .empty-illustration {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: var(--el-color-primary-light-9);
            color: var(--el-color-primary);
            margin-bottom: 10px;
        }

        .empty-title {
            font-size: 14px;
            font-weight: 500;
            color: var(--el-text-color-primary);
        }

        .empty-sub {
            margin-top: 4px;
            font-size: 12px;
            color: var(--el-text-color-secondary);
        }
    }

    .panel-section {
        & + .panel-section {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid var(--el-border-color-lighter);
        }

        .section-title {
            margin-bottom: 6px;
            font-size: 12px;
            color: var(--el-text-color-secondary);
        }

        .section-empty {
            padding: 6px 2px;
            font-size: 12px;
            color: var(--el-text-color-placeholder);
        }
    }

    // 待办区：最近 3 条待办标题
    .todo-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 7px 10px;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--el-fill-color-light);
        }

        .todo-title {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 13px;
            color: var(--el-text-color-primary);
        }

        .todo-time {
            flex-shrink: 0;
            font-size: 12px;
            color: var(--el-text-color-placeholder);
        }
    }

    // 审批中心入口（待我审批 n）
    .approval-entry {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 10px;
        margin-bottom: 4px;
        border-radius: 6px;
        background-color: var(--el-fill-color-light);
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--el-color-primary-light-9);
        }

        .approval-label {
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 500;
            color: var(--el-text-color-primary);
        }
    }

    .message-list {
        max-height: 300px;
        overflow-y: auto;
    }

    .message-footer {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid var(--el-border-color-lighter);
        text-align: center;
    }

    .message-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 0;
        border-bottom: 1px solid var(--el-border-color-lighter);

        &:last-child {
            border-bottom: none;
        }

        .type-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            border-radius: 8px;
            margin-top: 1px;

            &.type-3 {
                background: var(--el-color-primary-light-9);
                color: var(--el-color-primary);
            }

            &.type-1 {
                background: var(--el-color-success-light-9);
                color: var(--el-color-success);
            }

            &.type-2 {
                background: var(--el-color-warning-light-9);
                color: var(--el-color-warning);
            }
        }

        .message-title {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: var(--el-text-color-primary);

            .title-text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-weight: 400;
            }

            &.unread .title-text {
                font-weight: 600;
            }

            .unread-dot {
                flex-shrink: 0;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: var(--el-color-danger);
            }
        }

        .message-content {
            font-size: 12px;
            color: var(--el-text-color-secondary);
            margin-top: 4px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .message-time {
            flex-shrink: 0;
            font-size: 12px;
            color: var(--el-text-color-placeholder);
            margin-top: 2px;
        }
    }
}

.recent-panel {
    .recent-list {
        max-height: 240px;
        overflow-y: auto;
    }

    .recent-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 4px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--el-fill-color-light);
        }

        .recent-label {
            font-size: 14px;
            color: var(--el-text-color-primary);
        }

        .recent-time {
            font-size: 12px;
            color: var(--el-text-color-placeholder);
        }
    }
}

.quick-panel {
    .quick-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .quick-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 12px 8px;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: var(--el-fill-color-light);
        }

        span {
            font-size: 12px;
            color: var(--el-text-color-primary);
        }
    }
}
</style>
